import React, { useEffect } from 'react'
import merge from 'lodash/merge'
import { Controller, useForm } from 'react-hook-form'
import { l, SectionMessage, TextInputV2 } from '@manychat/manyui'

import { useAppDispatch } from 'reduxTyped'
import { EntryControls } from 'apps/aiAutomation/components/KnowledgeEditor/components/EntryControls'
import { MetaInfo } from 'apps/aiAutomation/components/KnowledgeEditor/components/MetaInfo'
import { SourceTextEditor } from 'apps/aiAutomation/components/KnowledgeEditor/components/SourceTextEditor'
import { knowledgeEntryResolver } from 'apps/aiAutomation/lib/knowledge'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { ConfirmSource } from 'apps/aiAutomation/store/layout/types'
import { clearTextFromTags } from 'components/MCTextEditor/formatting/adapter'
import {
  KnowledgeErrorCode,
  LinkEntry,
  LinkEntryDraft,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'

interface PlainTextEditorFormValues {
  title: string
  text: string
}

interface PlainTextEditorProps {
  existingEntry: LinkEntry | null
  processedEntry: LinkEntry | null
  error: KnowledgeErrorCode | null
  isLoading: boolean

  onSubmit: (entry: LinkEntryDraft | LinkEntry, hasBeenChanged?: boolean) => void
  onRescan: () => void
  onBackToReadMode: () => void
}

export const PlainTextEditor = ({
  existingEntry,
  processedEntry,
  error,
  onSubmit,
  onRescan,
  onBackToReadMode,
  isLoading,
}: PlainTextEditorProps) => {
  const dispatch = useAppDispatch()
  const isNewEntry = !existingEntry

  const { formState, register, handleSubmit, control } = useForm<PlainTextEditorFormValues>({
    defaultValues: {
      text: processedEntry?.data.text || existingEntry?.data.text || '',
      title: existingEntry?.title || processedEntry?.title || '',
    },
    resolver: knowledgeEntryResolver,
  })

  const { ref: titleRef, ...titleProps } = register('title')

  useEffect(() => {
    const hasUnsavedChanges = Boolean(processedEntry) || formState.isDirty

    dispatch(
      AiAutomationLayoutActions.confirmChanged({
        show: hasUnsavedChanges,
        source: processedEntry ? ConfirmSource.PREVIEWED_LINK : null,
      }),
    )
  }, [dispatch, formState.isDirty, processedEntry])

  const onSave = ({ text, title }: PlainTextEditorFormValues) => {
    onSubmit(
      merge({}, existingEntry, processedEntry, { data: { text: clearTextFromTags(text) }, title }),
      formState.isDirty,
    )
  }

  return (
    <>
      {error && (
        <SectionMessage
          className="m-b-lg"
          variant="warning"
          title={l.translate("Couldn't rescan content")}
          description={l.translate(
            'AI will continue using your previously saved content. Try again later.',
          )}
        />
      )}

      {Boolean(processedEntry) && (
        <SectionMessage
          className="m-b-lg"
          variant="info"
          title={l.translate("Here's the content extracted from your page")}
          description={l.translate('Review and edit it before adding it to your knowledge base')}
        />
      )}

      <MetaInfo
        onRescan={onRescan}
        isNewEntry={isNewEntry}
        url={processedEntry?.data.url || existingEntry?.data.url}
        updatedAt={existingEntry?.updated_at}
      />

      <TextInputV2
        inputRef={titleRef}
        className="m-b-lg"
        {...titleProps}
        errorText={formState.errors.title?.message}
        invalid={Boolean(formState.errors.title?.message)}
        label={l.translate('What’s this about?')}
        placeholder={l.translate('e.g. Course curriculum')}
        data-test-id="ai-automation/knowledge-editor-title-input"
      />

      <Controller
        name="text"
        control={control}
        render={({ field, fieldState }) => (
          <SourceTextEditor
            className="m-b-lg"
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            readOnly={isLoading}
            invalid={fieldState.invalid}
            label={l.translate('Content')}
            disabled={isLoading}
            data-test-id="ai-automation/knowledge-editor-content-input"
            placeholder={l.translate(
              "Course format, what students learn, program length, pricing, and what's included...",
            )}
            errorText={fieldState.error?.message}
          />
        )}
      />

      <EntryControls
        isLoading={isLoading}
        entry={existingEntry}
        onBackToReadMode={onBackToReadMode}
        onSubmit={handleSubmit(onSave)}
        cancelText={!processedEntry ? l.translate('Cancel') : l.translate('Discard')}
      />
    </>
  )
}
